import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Grid, Stack, Button, Checkbox } from "@mui/material";
import PersonIcon from "@mui/icons-material/PersonTwoTone";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import Identity from "./Identity";
import CreditProfile from "./CreditProfile";
import Employer from "./Employer";
import AddEmployer from "./AddEmployer";
import { EmploymentIncomeVerification } from "./EmploymentIncomeVerification";
import CustomerContactDetails from "./CustomerContactDetails";
import CustomerPersonalDetails from "./CustomerPersonalDetails";
import CustomerPersonalProfile from "./CustomerPersonalProfile";
import CustomerPersonalFinances from "./CustomerPersonalFinances";
import CustomerPrivacyAgreement from "./CustomerPrivacyAgreement";
import CustomerFinancialStatement from "./CustomerFinancialStatement";
import CustomerOtherDocuments from "./CustomerOtherDocuments";
import { CustomerInviteActions } from "./CustomerInviteActions";
import { getIsConsumerOrPersonalApplication } from "../../constants";
import {
  applicationSelector,
  confirmCustomerData,
  entitySelector,
  removeCustomer,
  sendCustomerDetailsLinkToCustomer,
  userDetailsSelector,
} from "../../store/slices/applicationFormSlice";
import { userPermissionsSelector } from "../../store/slices/userSlice";
import { customerSelector } from "../../store/slices/customerSlice";
import { getCustomerRole } from "../Utils/helperFunction";

const CustomerDetails = React.memo(
  ({
    customer,
    applicationId,
    applicationType,
    index,
    fieldErrors,
    setFieldErrors,
    setIsAcknowledgementAccept,
    isAcknowledgementAccept,
  }) => {
    const dispatch = useDispatch();
    const entity = useSelector(entitySelector);
    const application = useSelector(applicationSelector);
    const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(applicationType);
    const permissions = useSelector(userPermissionsSelector);
    const { isCustomerLogin } = useSelector(customerSelector);
    const { confirmCustomerDataLoader } = useSelector(userDetailsSelector);
    const { accessComponents } = useAccessAppComponent();
    const {
      application: {
        customer: { isEquifaxConsumerReportEnabled, isBankStatementsEnabled },
      },
    } = accessComponents;
    const isDisableEdit = !permissions?.application?.edit || !application?.canEditApplication;
    const servicePermission = permissions?.services;
    const customerId = customer?._id;

    const [confirmRemoveCustomer, setConfirmRemoveCustomer] = useState({});

    const handle = {
      sendFormLinkCustomer: ({ customerId, applicationId, applicationType }) => {
        dispatch(sendCustomerDetailsLinkToCustomer({ customerId, applicationId, applicationType }));
      },
      confirmCustomerChanges: () => {
        // show btn invite customer button (linkSend false, customerSave false)
        // show btn if link send (linkSend true, customerSave false)
        // show confirm btn (linkSend true, customerSave true)
        // If broker want to resend then make all state as default (linkSend false, customerSave false)
        dispatch(
          confirmCustomerData({
            customerId: customer._id,
            data: {
              application: applicationId,
              isCustomerDetailsSaved: false, // If broker want to send link again
              isDetailsLinkSend: false, // If broker want to send link again
            },
          }),
        );
      },
    };

    const isSendLinkBtnDisable =
      !customer?.firstName ||
      !customer?.dateOfBirth ||
      customer?.emails?.length <= 0 ||
      customer?.phoneNumbers?.length <= 0;

    const { role, appointmentDate, shares = 0, employers } = customer;

    const numberOfShares = entity?.[0]?.shares?.[0]?.numberOfShares || 0;
    const customerShares = customer?.role === "Director" ? numberOfShares / shares : shares / numberOfShares;
    const sharesPercentage = customerShares * 100;

    let temp = applicationId + "-" + index;

    return (
      <Grid
        container
        item
        xs={12}
        style={{
          padding: "0 0 30px",
        }}
        id={`customer-${customer?._id}`}
      >
        <Grid item xs={12} md={2}>
          <Stack direction="row" style={{ marginBottom: "20px" }}>
            <PersonIcon style={{ marginRight: "15px", color: "#1d4164" }} />
            <Typography fontWeight={600}>
              {getCustomerRole(application.applicationType, application.entities, application.purpose)} #{index + 1}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            {role && (
              <Typography style={{ fontSize: "14px" }}>
                <strong>Role:</strong> <br />
                {role}
              </Typography>
            )}
            {appointmentDate && (
              <Typography style={{ fontSize: "14px" }}>
                <strong>Appointment date:</strong> <br />
                {appointmentDate}
              </Typography>
            )}
            {shares && numberOfShares ? (
              <Typography style={{ fontSize: "14px" }}>
                <strong>Shares:</strong> <br />
                {customer?.role === "Director" ? `${numberOfShares} / ${shares}` : `${shares} / ${numberOfShares}`} (
                {sharesPercentage.toFixed(2)}
                )%
              </Typography>
            ) : (
              <></>
            )}
          </Stack>
          {!isCustomerLogin && !confirmRemoveCustomer[customer._id] && (
            <Button
              variant="outlined"
              size="small"
              disabled={isDisableEdit}
              style={{
                padding: 1,
                margin: "10px 5px 0 0",
              }}
              onClick={() => setConfirmRemoveCustomer({ [customer._id]: true })}
            >
              Remove
            </Button>
          )}

          {customer?.isDetailsLinkSend && customer?.isCustomerDetailsSaved && (
            <Button
              variant="outlined"
              size="small"
              style={{
                padding: 1,
                margin: "10px 0 0 0",
              }}
              disabled={isSendLinkBtnDisable || confirmCustomerDataLoader}
              onClick={handle.confirmCustomerChanges}
            >
              {confirmCustomerDataLoader ? "Saving..." : "Confirm"}
            </Button>
          )}

          {confirmRemoveCustomer[customer._id] && (
            <>
              <Typography>Are you sure?</Typography>
              <Button
                size="small"
                variant="link"
                style={{
                  padding: 0,
                }}
                onClick={() => setConfirmRemoveCustomer({ [customer._id]: false })}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="link"
                style={{
                  padding: 0,
                  color: "#d32f2f",
                }}
                onClick={() => {
                  dispatch(
                    removeCustomer({
                      customerId: customer._id,
                      applicationId,
                    }),
                  );
                  setConfirmRemoveCustomer({ [customer._id]: false });
                  setFieldErrors({
                    customerTitle: "",
                    firstName: "",
                    lastName: "",
                    IDNumber: "",
                    dateOfBirth: "",
                    IDCardNumber: "",
                    phoneNumber: "",
                    emailAddress: "",
                    address: "",
                  });
                }}
              >
                Remove
              </Button>
            </>
          )}

          {!isCustomerLogin && customer?.isDetailsLinkSend && (
            <Button
              variant="outlined"
              size="small"
              style={{
                padding: 1,
                margin: "10px 0 0 0",
              }}
              disabled={isSendLinkBtnDisable}
              onClick={() =>
                handle.sendFormLinkCustomer({
                  customerId: customer._id,
                  applicationId,
                  applicationType,
                })
              }
            >
              Resend link
            </Button>
          )}

          <CustomerInviteActions
            customer={customer}
            applicationId={applicationId}
            applicationType={applicationType}
            isCustomerLogin={isCustomerLogin}
            isSendLinkBtnDisable={isSendLinkBtnDisable}
            isDisableEdit={isDisableEdit}
          />
        </Grid>

        <Grid container item xs={12} md={10} style={{ padding: "0 0 0 10px" }}>
          {/* Personal Details */}
          <CustomerPersonalDetails
            customer={customer}
            customerId={customerId}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            applicationId={applicationId}
            key={temp}
            isDisableEdit={isDisableEdit}
          />
          {/* Contact Details */}
          <CustomerContactDetails
            customerId={customerId}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            customer={customer}
            applicationId={applicationId}
            isDisableEdit={isDisableEdit}
          />
          {/* Personal Profile */}
          <CustomerPersonalProfile
            customer={customer}
            customerId={customerId}
            applicationId={applicationId}
            isDisableEdit={isDisableEdit}
          />
          {/* Privacy Agreement */}
          {customer && !isCustomerLogin && (
            <CustomerPrivacyAgreement
              customer={customer}
              applicationId={applicationId}
              applicationType={application?.applicationType}
              isDisableEdit={isDisableEdit}
            />
          )}
          {/* Employer */}
          {isConsumerOrPersonalApplication &&
            employers?.map((employer, i) => (
              <Employer
                isDisableEdit={isDisableEdit}
                key={employer.id}
                customer={customer}
                employer={employer}
                index={i}
                customerId={customerId}
                setFieldErrors={setFieldErrors}
                applicationId={applicationId}
                customerIndex={index}
              />
            ))}
          {/* Add Employer */}
          {isConsumerOrPersonalApplication && (
            // (!customer?.employers?.[0] || timeOfEmploymentTotal < 12) &&
            <AddEmployer
              customerId={customer._id}
              applicationId={applicationId}
              numberOfEmployers={customer?.employers?.length}
              isDisableEdit={isDisableEdit}
            />
          )}
          {/* Employment & Income Verification */}
          {customer && isConsumerOrPersonalApplication && !isCustomerLogin && (
            <EmploymentIncomeVerification
              customer={customer}
              applicationId={applicationId}
              isDisableEdit={isDisableEdit}
            />
          )}
          {/* Personal Finances */}
          <CustomerPersonalFinances
            customer={customer}
            customerId={customerId}
            customerIndex={index}
            applicationType={applicationType}
            applicationId={applicationId}
            isDisableEdit={isDisableEdit}
          />
          {/* Identity */}
          {customer && servicePermission?.id_verification && !isCustomerLogin && (
            <Identity customer={customer} applicationId={applicationId} isDisableEdit={isDisableEdit} />
          )}
          {/* Credit Profile */}
          {customer &&
            isEquifaxConsumerReportEnabled &&
            servicePermission?.equifax_basic_report &&
            !isCustomerLogin && (
              <CreditProfile
                customer={customer}
                applicationId={applicationId}
                isDisableEdit={isDisableEdit}
                customerIndex={index}
              />
            )}
          {/* Bank Statement */}
          {customer && isBankStatementsEnabled && servicePermission?.bank_statements && !isCustomerLogin && (
            <Grid
              item
              xs={12}
              style={{
                margin: "0 0 30px",
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                paddingBottom: "30px",
              }}
            >
              {/* Customer Financial Statement */}
              <CustomerFinancialStatement
                customer={customer}
                applicationId={applicationId}
                isDisableEdit={isDisableEdit}
              />
            </Grid>
          )}
          {/* Customer Other Documents */}
          <CustomerOtherDocuments customer={customer} applicationId={applicationId} />
          {isCustomerLogin && (
            <Stack sx={{ flexDirection: "row", alignItems: "start" }}>
              <Checkbox
                value={isAcknowledgementAccept}
                onChange={(event) => setIsAcknowledgementAccept(event.target.checked)}
              />
              <Typography sx={{ fontSize: "0.80rem", marginBottom: "50px", paddingTop: "7px" }}>
                {customer?.applications?.[0]?.customer_acknowledgement?.acknowledgement_text}
              </Typography>
            </Stack>
          )}
        </Grid>
      </Grid>
    );
  },
);

export default CustomerDetails;
